import { useMemo } from 'react';

import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    AREA_CHART_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import { useMetricColor } from '../../../utilities/ColorUtilities';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { timeRangeFilterId, virtualMachineFilterId } from '../../filters';

const title = t`Virtual Machine Disk I/O`;

const dataSource = {
    url: '/query/chart',
    params: {
        type: 'server',
        metrics: ['vmDiskReadsBps', 'vmDiskWritesBps'],
    },
};

function useChartConfig() {
    const readColor = useMetricColor('serverDiskOut');
    const writeColor = useMetricColor('serverDiskIn');

    return useMemo(
        () => ({
            yAxis: [
                {
                    title: {
                        text: t`Utilization (Bytes/s)`,
                    },
                },
            ],
            chartType: 'areaspline',
            stacking: 'normal',
            tooltip: BuiltInTooltip,
            showGranularity: true,
            chartTrends: [
                {
                    formatter: FORMATTER_TYPES.LOG,
                    units: ` ${t`Bytes/s`}`,
                    chartType: 'areaspline',
                    key: 'vmDiskWritesBps',
                    label: t`Write`,
                    color: writeColor,
                    multiplier: 1,
                    forceDecimals: true,
                },
                {
                    formatter: FORMATTER_TYPES.LOG,
                    units: ` ${t`Bytes/s`}`,
                    chartType: 'areaspline',
                    key: 'vmDiskReadsBps',
                    label: t`Read`,
                    color: readColor,
                    multiplier: 1,
                    forceDecimals: true,
                },
            ],
        }),
        [readColor, writeColor]
    );
}

function VirtualMachineDiskIOPanel() {
    const data = usePanelDataService(dataSource);
    const chartConfig = useChartConfig();
    const chartData = data?.data?.[0];

    if (!data) return null;

    return <PerformanceChart chartDef={chartConfig} data={chartData} />;
}

VirtualMachineDiskIOPanel.propTypes = panelPropTypes;

VirtualMachineDiskIOPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'VmwareVmDiskThroughputTrend',
    catalogEntry: {
        category: PANEL_CATEGORIES.VIRTUAL_MACHINE,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`Disk I/O trend for a selected virtual machine (VM)`,
        previewImage: AREA_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, virtualMachineFilterId],
        optional: [],
    },
})(VirtualMachineDiskIOPanel);
